import {
  UzButton,
  UzCard,
  UzDatePicker,
  UzInput,
  UzNumberInput,
  UzSelect,
  type UzSelectOption,
} from '@utazzo/ui';
import { Plus, Trash2 } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { formatInrPaise } from '../invoices.helpers';

import {
  emptySupplierRow,
  rupeesToPaiseNum,
  supplierRowGrossPaise,
  type SupplierRow,
} from './invoice-form.helpers';
import { SupplierChangeReasonDialog } from './SupplierChangeReasonDialog';

import { SupplierPicker } from '@/components/pickers/SupplierPicker';
import { useCurrencyOptions } from '@/features/suppliers/use-suppliers';

interface SupplierLinesGridProps {
  rows: SupplierRow[];
  onChange: (rows: SupplierRow[]) => void;
}

/**
 * Section 11 - Supplier detail (optional). Per-row gross is payable +
 * commission - TDS (readonly). Footer shows running payable / gross /
 * commission / TDS totals; the payable total feeds the live GST preview's
 * `supplierPayablePaise`.
 */
export const SupplierLinesGrid = ({
  rows,
  onChange,
}: SupplierLinesGridProps): React.JSX.Element => {
  const update = (idx: number, patch: Partial<SupplierRow>): void =>
    onChange(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  // Supplier pick awaiting a reason (line already has payments).
  const [pendingChange, setPendingChange] = useState<{ idx: number; supplierId: string } | null>(
    null,
  );

  const handleSupplierChange = (idx: number, next: string): void => {
    const row = rows[idx];
    if (!row || next === row.supplierId) return;
    if (next === (row.originalSupplierId ?? '')) {
      // Back to the original supplier: no change to approve.
      update(idx, { supplierId: next, supplierChangeReason: undefined });
      return;
    }
    if (row.id && (row.livePaymentCount ?? 0) > 0) {
      setPendingChange({ idx, supplierId: next });
      return;
    }
    update(idx, { supplierId: next });
  };

  const currencyQuery = useCurrencyOptions();
  const currencyOptions = useMemo<ReadonlyArray<UzSelectOption>>(
    () => (currencyQuery.data ?? []).map((c) => ({ value: c.id, label: c.code })),
    [currencyQuery.data],
  );

  const totals = rows.reduce(
    (acc, r) => {
      acc.payable += rupeesToPaiseNum(r.payable);
      acc.tds += rupeesToPaiseNum(r.tds);
      acc.gross += supplierRowGrossPaise(r);
      return acc;
    },
    { payable: 0, tds: 0, gross: 0 },
  );

  return (
    <UzCard
      title="Supplier detail"
      description="Your buying cost per supplier. Optional."
      actions={
        <div className="flex items-center gap-3">
          <a
            className="text-xs text-blue-600 hover:text-blue-700"
            href="/suppliers"
            target="_blank"
            rel="noreferrer"
          >
            Create supplier
          </a>
          <UzButton
            variant="outline"
            size="sm"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => onChange([...rows, emptySupplierRow()])}
          >
            Add supplier
          </UzButton>
        </div>
      }
    >
      {rows.length === 0 ? (
        <p className="text-sm text-zinc-500">No supplier lines added.</p>
      ) : (
        <div className="space-y-5">
          {rows.map((row, idx) => {
            const grossPaise = supplierRowGrossPaise(row);
            return (
              <div key={idx} className="rounded-md border border-zinc-100 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-medium text-zinc-500">
                    Supplier {idx + 1}
                    {row.supplierChangeReason ? (
                      <span className="ml-2 rounded bg-amber-50 px-1.5 py-0.5 text-amber-700">
                        Change will be sent for approval
                      </span>
                    ) : null}
                  </span>
                  <UzButton
                    variant="ghost"
                    size="sm"
                    aria-label="Remove supplier line"
                    onClick={() => onChange(rows.filter((_, i) => i !== idx))}
                  >
                    <Trash2 className="h-4 w-4" />
                  </UzButton>
                </div>
                <div className="grid gap-3 md:grid-cols-4">
                  <SupplierPicker
                    label="Supplier"
                    value={row.supplierId || null}
                    onValueChange={(v) => handleSupplierChange(idx, v ?? '')}
                  />
                  <UzInput
                    label="Description"
                    value={row.description}
                    onChange={(e) => update(idx, { description: e.target.value })}
                  />
                  <UzDatePicker
                    label="Payment due date"
                    value={row.paymentDueDate}
                    onValueChange={(v) => update(idx, { paymentDueDate: v })}
                  />
                  <UzNumberInput
                    label="Payable (Rs)"
                    min={0}
                    value={row.payable}
                    onValueChange={(v) => update(idx, { payable: v ?? '' })}
                  />
                  <UzNumberInput
                    label="CGST (Rs)"
                    min={0}
                    value={row.cgst}
                    onValueChange={(v) => update(idx, { cgst: v ?? '' })}
                  />
                  <UzNumberInput
                    label="SGST (Rs)"
                    min={0}
                    value={row.sgst}
                    onValueChange={(v) => update(idx, { sgst: v ?? '' })}
                  />
                  <UzNumberInput
                    label="IGST (Rs)"
                    min={0}
                    value={row.igst}
                    onValueChange={(v) => update(idx, { igst: v ?? '' })}
                  />
                  <UzNumberInput
                    label="TDS (Rs)"
                    min={0}
                    value={row.tds}
                    onValueChange={(v) => update(idx, { tds: v ?? '' })}
                  />
                  <UzSelect
                    label="Currency"
                    placeholder={currencyQuery.isLoading ? 'Loading...' : 'Select currency'}
                    options={currencyOptions}
                    value={row.currencyId || undefined}
                    onValueChange={(v) => update(idx, { currencyId: v })}
                    disabled={currencyQuery.isLoading}
                  />
                  <UzNumberInput
                    label="Exchange rate"
                    precision={4}
                    min={0}
                    value={row.exchangeRate}
                    onValueChange={(v) => update(idx, { exchangeRate: v ?? '' })}
                  />
                  <UzNumberInput
                    label="No. of currency"
                    precision={0}
                    min={0}
                    value={row.noOfCurrency}
                    onValueChange={(v) => update(idx, { noOfCurrency: v ?? '' })}
                  />
                </div>
                <div className="mt-2 flex justify-end text-sm">
                  <span className="text-zinc-500">
                    Gross (payable - TDS):{' '}
                    <span className="font-medium text-zinc-800 tabular-nums">
                      {formatInrPaise(String(grossPaise))}
                    </span>
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-4 flex flex-wrap justify-end gap-6 border-t border-zinc-100 pt-3 text-sm">
        <span className="text-zinc-500">
          Payable:{' '}
          <span className="font-medium text-zinc-800 tabular-nums">
            {formatInrPaise(String(totals.payable))}
          </span>
        </span>
        <span className="text-zinc-500">
          TDS:{' '}
          <span className="font-medium text-zinc-800 tabular-nums">
            {formatInrPaise(String(totals.tds))}
          </span>
        </span>
        <span className="text-zinc-500">
          Gross:{' '}
          <span className="font-medium text-zinc-800 tabular-nums">
            {formatInrPaise(String(totals.gross))}
          </span>
        </span>
      </div>
      <SupplierChangeReasonDialog
        open={pendingChange !== null}
        onCancel={() => setPendingChange(null)}
        onConfirm={(reason) => {
          if (pendingChange) {
            update(pendingChange.idx, {
              supplierId: pendingChange.supplierId,
              supplierChangeReason: reason,
            });
          }
          setPendingChange(null);
        }}
      />
    </UzCard>
  );
};
